/**
 * leaveGuard — lets the page on screen ask "discard unsaved changes?" before
 * the user leaves in a way the router cannot block: signing out.
 *
 * Route changes are guarded with React Router's `useBlocker` in the page
 * itself. Sign-out is different — it clears the session (and the token) the
 * moment it is dispatched, so asking has to happen BEFORE, from the sign-out
 * code. This is the meeting point: one page at a time registers a guard while
 * it has work to lose, and sign-out awaits `confirmLeave()` first.
 *
 * Module state, not Redux: a function is not state, and nothing renders from it.
 * Imports nothing, so it cannot form an import cycle.
 */

/** @type {null | (() => Promise<boolean>)} */
let guard = null;

/**
 * Register the guard; returns the function that removes it (only if it is
 * still the registered one — a newer page's guard is never removed by an older).
 */
export function setLeaveGuard(fn) {
  guard = fn;
  return () => {
    if (guard === fn) guard = null;
  };
}

/** Resolves true when it is fine to leave (no guard, or the user said Yes). */
export function confirmLeave() {
  return guard ? guard() : Promise.resolve(true);
}
